import React, { useState, useRef, useEffect, useMemo } from 'react'
import { MessageSquareMore, ChevronDown } from 'lucide-react'
import { SectionCard } from '../../sections/SectionCard'
import { PANEL_SPACING } from '../../../constants/spacing'
import { Button } from '@shared/components/ui/button'
import { ActivityItem } from './ActivityItem'
import { AddCommentDialog } from './AddCommentDialog'
import { ActionDialog } from './ActionDialog'
import type {
  ActivityEvent,
  InformationalActivityEvent,
  ActivitySectionProps,
  ActionRequiredActivityEvent,
  AddCommentFormData,
} from './types'
import { isActionRequired } from './types'
import { buildOperatorResponse } from './activity-response-builder'

// ─────────────────────────────────────────────────────────────────────────────
// Attention count badge (red circle with number, matches Figma design)
// ─────────────────────────────────────────────────────────────────────────────

function AttentionCountBadge({ count }: { count: number }) {
  if (count === 0) return null
  return (
    <div className="w-[14px] h-[14px] rounded-full bg-red-600 flex items-center justify-center">
      <span className="text-[9px] font-semibold text-white leading-none tracking-[-0.09px]">
        {count}
      </span>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Default number of events to show before "View more"
// ─────────────────────────────────────────────────────────────────────────────

const INITIAL_VISIBLE_COUNT = 2

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export const PaymentActivitySection: React.FC<ActivitySectionProps> = ({
  events,
  title = 'Activity',
  className,
  onActionClick: externalActionHandler,
  showAddComment = true,
  scrollIntoView: shouldScrollIntoView,
  autoExpandEventId,
}) => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const [localEvents, setLocalEvents] = useState<ActivityEvent[]>(events)
  const [commentOpen, setCommentOpen] = useState(false)
  const [actionEvent, setActionEvent] = useState<ActionRequiredActivityEvent | null>(null)
  const [actionOpen, setActionOpen] = useState(false)
  const [showAll, setShowAll] = useState(false)

  // Sync local state when prop changes (e.g. parent re-fetches)
  useEffect(() => {
    setLocalEvents(events)
  }, [events])

  useEffect(() => {
    if (shouldScrollIntoView && sectionRef.current) {
      const timer = setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 150)
      return () => clearTimeout(timer)
    }
  }, [shouldScrollIntoView])

  useEffect(() => {
    if (autoExpandEventId) {
      setShowAll(true)
    }
  }, [autoExpandEventId])

  // Sort events by timestamp descending (most recent first)
  const sortedEvents = useMemo(
    () => [...localEvents].sort((a, b) => b.timestamp.localeCompare(a.timestamp)),
    [localEvents],
  )

  if (sortedEvents.length === 0) return null

  // Count unresolved action-required events
  const attentionCount = sortedEvents.filter(
    (e) => isActionRequired(e) && !e.actionCompleted
  ).length

  // Determine visible events
  const hasMore = sortedEvents.length > INITIAL_VISIBLE_COUNT
  const visibleEvents: ActivityEvent[] =
    showAll || !hasMore ? sortedEvents : sortedEvents.slice(0, INITIAL_VISIBLE_COUNT)

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleActionClick = (event: ActivityEvent) => {
    if (isActionRequired(event)) {
      if (externalActionHandler) {
        externalActionHandler(event)
        return
      }
      setActionEvent(event)
      setActionOpen(true)
    }
  }

  const handleActionSuccess = (resolvedEvent: ActionRequiredActivityEvent, data: unknown) => {
    const response = buildOperatorResponse(resolvedEvent, data)
    setLocalEvents((prev) =>
      prev.map((e) =>
        e.id === resolvedEvent.id
          ? ({ ...e, actionCompleted: true, operatorResponse: response } as ActionRequiredActivityEvent)
          : e
      )
    )
    setActionOpen(false)
    setActionEvent(null)
  }

  const handleCommentSubmit = async (data: AddCommentFormData) => {
    // Mock implementation — replace with API call when ready
    await new Promise((r) => setTimeout(r, 500))

    const now = new Date()
    const newComment: InformationalActivityEvent = {
      id: `comment-${Date.now()}`,
      type: 'comment_added',
      title: data.isInternal ? 'Internal note added' : 'Comment added',
      description: data.comment,
      timestamp: now.toISOString(),
      timestampDisplay: now.toLocaleDateString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
      }) + ' ' + now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
      severity: 'info',
      requiresAction: false,
      icon: 'message-square',
    }
    setLocalEvents((prev) => [newComment, ...prev])
  }

  // ── Title with inline attention badge ──────────────────────────────────────

  const sectionTitle = (
    <span className="flex items-center gap-2">
      {title}
      <AttentionCountBadge count={attentionCount} />
    </span>
  )

  // ── Header actions ────────────────────────────────────────────────────────

  const headerActions = showAddComment ? (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-8"
      onClick={() => setCommentOpen(true)}
    >
      <MessageSquareMore className="size-4" />
    </Button>
  ) : undefined

  // ── Timeline content ──────────────────────────────────────────────────────

  const timelineContent = (
    <div className={`${PANEL_SPACING.CONTENT_RIGHT_WIDTH} ml-auto flex flex-col`}>
      {visibleEvents.map((event, index) => {
        const isLast = index === visibleEvents.length - 1
        return (
          <ActivityItem
            key={event.id}
            event={event}
            isLast={isLast}
            showLine={!isLast}
            onActionClick={handleActionClick}
          />
        )
      })}

      {/* "View more" button when there are additional events */}
      {hasMore && !showAll && (
        <div className="pl-10">
          <Button
            variant="link"
            size="sm"
            className="h-9 px-4 text-[14px] font-medium text-emerald-600 tracking-[-0.14px] hover:no-underline"
            onClick={() => setShowAll(true)}
          >
            View more
            <ChevronDown className="size-4 ml-1 text-emerald-600" />
          </Button>
        </div>
      )}
    </div>
  )

  return (
    <>
      <div ref={sectionRef}>
        <SectionCard
          title={sectionTitle}
          layout="down"
          headerActions={headerActions}
          className={className}
        >
          {timelineContent}
        </SectionCard>
      </div>

      {/* Dialogs rendered outside SectionCard to avoid z-index issues */}
      <AddCommentDialog
        open={commentOpen}
        onClose={() => setCommentOpen(false)}
        onSubmit={handleCommentSubmit}
      />

      <ActionDialog
        event={actionEvent}
        open={actionOpen}
        onClose={() => {
          setActionOpen(false)
          setActionEvent(null)
        }}
        onSuccess={handleActionSuccess}
      />
    </>
  )
}
